import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import { useQuery } from '@tanstack/react-query';

export const coachStateQueryKey = (weddingId: string | undefined) => [
  'ai-planner',
  'coach-state',
  weddingId,
];

export type CoachState = {
  // Undefined while in flight, or when there is no store.
  projectionAnchorAt: number | undefined;
  budgetExcludedCategories: string[] | undefined;
};

export const EMPTY_COACH_STATE: CoachState = {
  projectionAnchorAt: undefined,
  budgetExcludedCategories: undefined,
};

const fetchCoachState = async (): Promise<CoachState> => {
  const currentUser = await getCurrentUser();
  const token = currentUser ? await currentUser.getIdToken() : null;
  const response = await fetch('/api/ai-planner/coach-state', {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    return EMPTY_COACH_STATE;
  }
  const body = (await response.json()) as {
    projectionAnchorAt: number | null;
    budgetExcludedCategories: string[] | null;
  };
  return {
    projectionAnchorAt: body.projectionAnchorAt ?? undefined,
    budgetExcludedCategories: body.budgetExcludedCategories ?? undefined,
  };
};

/**
 * The wedding's coach memory: the checklist projection's anchor, set on first read, and any
 * budget assumption the couple corrected. Both are optional: while the request is in flight,
 * the projections derive what they need for this turn.
 */
export const useCoachStateQuery = (
  weddingId: string | undefined,
): { coachState: CoachState; settled: boolean; failed: boolean } => {
  const active = Boolean(weddingId);
  const { data, isFetched, isError } = useQuery(coachStateQueryKey(weddingId), fetchCoachState, {
    enabled: active,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });

  // Settled means the one attempt is over, answered or failed, and a read nobody asked for
  // has nothing to wait on. A refused response reads as empty rather than as a failure, so
  // only a request that never completed is one.
  return { coachState: data ?? EMPTY_COACH_STATE, settled: !active || isFetched, failed: isError };
};

export const useCoachState = (weddingId: string | undefined): CoachState =>
  useCoachStateQuery(weddingId).coachState;
